/** One test-rendered channel: header chips, subject, body preview and the
 *  composition provenance. Extracted from TestRenderPage for reuse in the
 *  notification editor's TestRenderDialog.
 *
 *  The rendered email body is untrusted template output — RenderedBodyView
 *  displays it ONLY inside a fully sandboxed iframe (no scripts, no
 *  same-origin, no navigation).
 */
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { RENDER_CHANNELS, type RenderChannel, type TestRenderChannelResult } from '@/api/notificationTestRender';

import { ProvenancePanel } from './ProvenancePanel';
import { RenderedBodyView } from './RenderedBodyView';

function channelLabel(channel: RenderChannel): string {
  return RENDER_CHANNELS.find((c) => c.value === channel)?.label ?? channel;
}

export function ChannelResultCard({ res }: { res: TestRenderChannelResult }) {
  const [view, setView] = useState<'preview' | 'raw'>('preview');
  const isEmail = res.channel === 'email';

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Chip size="small" color="primary" label={channelLabel(res.channel)} />
        <Typography variant="body2" color="text.secondary">
          Delivery method #{res.delivery_method}
        </Typography>
        <Chip size="small" variant="outlined" label={res.format} />
        {isEmail && res.body != null && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={view}
            onChange={(_, v: 'preview' | 'raw' | null) => v && setView(v)}
            sx={{ ml: 'auto' }}
          >
            <ToggleButton value="preview">Preview</ToggleButton>
            <ToggleButton value="raw">Raw</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      {res.error && <Alert severity="error">{res.error}</Alert>}

      {res.subject != null && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          <Box component="span" sx={{ color: 'text.secondary' }}>
            Subject:{' '}
          </Box>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {res.subject}
          </Box>
        </Typography>
      )}

      {res.body != null && (
        <RenderedBodyView
          body={res.body}
          preview={isEmail && view === 'preview'}
          title={`Rendered email (delivery method ${res.delivery_method})`}
        />
      )}

      {res.composition && <ProvenancePanel composition={res.composition} />}
    </Paper>
  );
}
